import { useEffect, useState } from "react";
import { apiPublic, mesajEroare } from "./api.js";
import { um } from "./um.js";
import { tr } from "./i18n.js";

/**
 * Pagina pe care o deschide clientul din link. Nu are bară de aplicație, nu are
 * meniu și nu are sesiune: cine ajunge aici n-are cont. Arată STRICT oferta.
 */
const lei = (v, moneda = "RON") =>
  v == null ? "—" : `${Number(v).toLocaleString("ro-RO", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })} ${moneda}`;

export default function OfertaPublica({ token }) {
  const [oferta, setOferta] = useState(null);
  const [eroare, setEroare] = useState("");
  const [nume, setNume] = useState("");
  const [email, setEmail] = useState("");
  const [motiv, setMotiv] = useState("");
  const [refuz, setRefuz] = useState(false);
  const [lucreaza, setLucreaza] = useState(false);

  const incarca = () => apiPublic.oferta(token).then(setOferta)
    .catch((e) => setEroare(mesajEroare(e)));

  useEffect(() => { incarca(); }, [token]);

  if (eroare && !oferta) {
    return (
      <div className="pagina-publica">
        <div className="card">
          <h2>{tr("Linkul nu mai e valid")}</h2>
          <p className="subtitlu">{eroare}</p>
          <p>
            {tr("Dacă ai primit între timp o ofertă revizuită, folosește linkul din mesajul cel mai nou — cel vechi încetează să meargă în clipa în care se emite altul.")}
          </p>
        </div>
      </div>
    );
  }
  if (!oferta) return <div className="gol" style={{ paddingTop: "25vh" }}>{tr("Se încarcă oferta…")}</div>;

  const s = oferta.liniiSnapshot;

  async function decide(accept) {
    setLucreaza(true); setEroare("");
    try {
      await apiPublic.decide(token, accept, {
        numeDecident: nume.trim(),
        ...(email.trim() ? { emailDecident: email.trim() } : {}),
        ...(accept ? {} : { motivRefuz: motiv.trim() }),
      });
      await incarca();
      setRefuz(false);
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  const gata = nume.trim().length >= 3 && (!refuz || motiv.trim().length >= 5);

  return (
    <div className="pagina-publica">
      <div className="card card-oferta">
        <div className="oferta-antet">
          <div>
            <h2>{tr(oferta.titlu)}</h2>
            <p className="subtitlu">
              {tr("Ofertă")} <code>{oferta.numar}</code> {tr("de la")} {tr(oferta.tenant?.denumire)} {tr("pentru")}{" "}
              {tr(oferta.client?.denumire)}
            </p>
          </div>
          <div className="oferta-total">
            <span>{tr("Total cu TVA")}</span>
            <strong>{lei(oferta.total, oferta.moneda)}</strong>
          </div>
        </div>

        {oferta.descriere && <p style={{ whiteSpace: "pre-wrap" }}>{tr(oferta.descriere)}</p>}

        {eroare && <div className="eroare">{eroare}</div>}

        {oferta.expirata && (
          <div className="panou panou-atentie"><div className="panou-corp">
            <strong>{tr("Oferta a expirat.")}</strong> {tr("Prețurile se recalculează la ziua emiterii, deci nu mai poate fi acceptată așa cum e. Cere una nouă.")}
          </div></div>
        )}

        {oferta.decisLa && (
          <div className="panou panou-atentie"><div className="panou-corp">
            <strong>{oferta.status === "Acceptata" ? tr("Ofertă acceptată") : tr("Ofertă refuzată")}</strong>{" "}
            {tr("de")} {oferta.decisDeNume}{tr(", la")} {new Date(oferta.decisLa).toLocaleString("ro-RO")}.
            {oferta.motivRefuz && <div>{tr("Motiv:")} {oferta.motivRefuz}</div>}
          </div></div>
        )}

        {/* Pagina asta se deschide de pe telefonul clientului, nu de pe un
            monitor. Tabelul de linii e mai lat decât un ecran de 390px, deci
            se derulează ÎN cutia lui — altfel se revărsa peste marginea
            cardului, până în afara paginii. */}
        <div className="tabel-derulabil">
        <table className="tabel tabel-catalog">
          <thead>
            <tr>
              <th>{tr("Poziție")}</th><th className="num">{tr("Cantitate")}</th><th>UM</th>
              <th className="num">{tr("Preț unitar")}</th><th className="num">{tr("Valoare")}</th><th className="num">TVA</th>
            </tr>
          </thead>
          <tbody>
            {(s?.linii ?? []).map((l, i) => (
              <tr key={i}>
                <td>{tr(l.denumire)} <span className="cod-mic">{l.cod}</span></td>
                <td className="num">{Number(l.cantitate).toLocaleString("ro-RO")}</td>
                <td>{um(l.unitate)}</td>
                <td className="num">{lei(l.pretUnitar, s?.moneda)}</td>
                <td className="num">{lei(l.valoare, s?.moneda)}</td>
                <td className="num">{l.cotaTVA}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {/* Totalurile stau ÎN AFARA cutiei care se derulează. Ca rânduri de
            tfoot, cu patru celule goale înaintea lor, erau împinse în dreapta:
            pe telefon clientul vedea trei rânduri albe și nicio sumă — exact
            cifra pentru care deschide pagina. */}
        <dl className="sume-oferta">
          <div><dt>{tr("fără TVA")}</dt><dd>{lei(s?.subtotal, s?.moneda)}</dd></div>
          <div><dt>TVA</dt><dd>{lei(s?.tva, s?.moneda)}</dd></div>
          <div className="sume-total"><dt>{tr("total")}</dt><dd>{lei(s?.total, s?.moneda)}</dd></div>
        </dl>

        <p className="nota-cautare">
          {tr("Valabilă până la")}{" "}
          {oferta.valabilPanaLa
            ? new Date(oferta.valabilPanaLa).toLocaleDateString("ro-RO") : "—"}{tr(". Amprenta conținutului:")} <code>{String(oferta.hashContinut ?? "").slice(0, 16)}…</code>
        </p>

        {oferta.sePoateDecide && (
          <div className="panou">
            <div className="panou-antet">{tr("Decizia dumneavoastră")}</div>
            <div className="panou-corp">
              <div className="formular-linie">
                <label style={{ flex: 1 }}>{tr("Nume și prenume")}
                  <input className="camp" value={nume} onChange={(e) => setNume(e.target.value)}
                    placeholder={tr("Cine decide")} />
                </label>
                <label style={{ flex: 1 }}>{tr("Email (opțional)")}
                  <input className="camp" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} />
                </label>
              </div>

              {refuz && (
                <label className="camp">{tr("Motivul refuzului")}
                  <textarea className="camp-textarea" rows={3} value={motiv}
                    onChange={(e) => setMotiv(e.target.value)}
                    placeholder={tr("Un refuz fără motiv nu se poate urmări.")} />
                </label>
              )}

              <p className="nota-cautare">
                {tr("Decizia se consemnează cu numele, momentul și adresa de internet de la care a fost dată — ea ține locul unei semnături, fiindcă nu există un cont în spate care s-o susțină.")}
              </p>

              <div className="actiuni-catalog">
                <button className="buton buton-actiune" disabled={lucreaza || !gata || refuz}
                  onClick={() => decide(true)}>
                  {tr("accept oferta")}
                </button>
                {refuz ? (
                  <>
                    <button className="buton buton-sters" disabled={lucreaza || !gata}
                      onClick={() => decide(false)}>
                      {tr("trimite refuzul")}
                    </button>
                    <button className="buton buton-mic buton-discret" onClick={() => setRefuz(false)}>
                      {tr("înapoi")}
                    </button>
                  </>
                ) : (
                  <button className="buton buton-sters" onClick={() => setRefuz(true)}>
                    {tr("nu accept")}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <footer className="subsol">{tr("Modul Mentenanță Tehnică · platforma CentralSec")}</footer>
    </div>
  );
}
